import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FaRegFilePdf } from 'react-icons/fa6';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/apiUrl';
import { downloadPdfById } from '../../utils/downloadPdf';
import '../../assets/scss/profile.scss';

const Profile = () => {
	const { user: authUser, isLoading: authLoading } = useAuth();
	const [userInfo, setUserInfo] = useState(null);
	const [error, setError] = useState(null);
	const [isEditing, setIsEditing] = useState(false);
	const [activeSection, setActiveSection] = useState('userInfo');
	const [profileMessage, setProfileMessage] = useState('');
	const [passwordData, setPasswordData] = useState({
		currentPassword: '',
		newPassword: '',
		confirmNewPassword: '',
	});
	const [passwordMessage, setPasswordMessage] = useState('');
	const [userPDFs, setUserPDFs] = useState([]);

	const offices = [
		'Oslo',
		'Hamar',
		'Trondheim',
		'Molde',
		'Ålesund',
		'Stavanger',
	];

	useEffect(() => {
		if (authLoading) return;

		if (!authUser?.id) {
			setError('Ikke innlogget');
			return;
		}

		setError(null);
		const userId = authUser.id;

		const fetchUserInfo = async () => {
			try {
				const { data } = await axios.post(
					buildApiUrl('auth/profile'),
					{ userId }
				);
				setUserInfo(data);
				await fetchUserPDFs();
			} catch (err) {
				const msg =
					err.response?.data?.message ||
					'Kunne ikke hente brukerinformasjon.';
				setError(msg);
			}
		};

		const fetchUserPDFs = async () => {
			try {
				const { data } = await axios.get(buildApiUrl('pdfs/user-pdfs'), {
					params: { page: 1, pageSize: 50 },
				});
				setUserPDFs(Array.isArray(data.items) ? data.items : []);
			} catch (err) {
				const msg =
					err.response?.data?.message ||
					err.response?.data?.error ||
					'Kunne ikke hente dine PDF-er.';
				setError(msg);
			}
		};

		fetchUserInfo();
	}, [authLoading, authUser?.id]);

	const handleEditClick = () => {
		setIsEditing(true);
	};

	const handleAbortClick = () => {
		setIsEditing(false);
	};

	const handleConfirmClick = async () => {
		try {
			const userId = userInfo.id;

			const { data } = await axios.patch(
				buildApiUrl(`auth/users/${userId}`),
				userInfo
			);
			setUserInfo(data);
			setIsEditing(false);
			setProfileMessage('Informasjonen din er oppdatert');
		} catch (err) {
			const msg =
				err.response?.data?.message ||
				'Kunne ikke oppdatere brukerinformasjon.';
			setError(msg);
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setUserInfo((prevInfo) => ({
			...prevInfo,
			[name]: value,
		}));
	};

	const handlePasswordChange = (e) => {
		const { name, value } = e.target;
		setPasswordData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handlePasswordSubmit = async (e) => {
		e.preventDefault();
		try {
			const userId = userInfo.id;

			await axios.patch(
				buildApiUrl(`auth/users/${userId}/password`),
				passwordData
			);
			setPasswordMessage('Passordet er oppdatert');
			setPasswordData({
				currentPassword: '',
				newPassword: '',
				confirmNewPassword: '',
			});
		} catch (err) {
			const msg =
				err.response?.data?.message ||
				'Kunne ikke oppdatere passord.';
			setPasswordMessage(msg);
		}
	};

	if (authLoading) {
		return <p>Laster...</p>;
	}

	if (error) {
		return <p>{error}</p>;
	}

	if (!userInfo) {
		return <p>Laster...</p>;
	}

	const currentOffice = userInfo.department;
	const otherOffices = offices.filter((office) => office !== currentOffice);

	return (
		<div className='profile'>
			<h1>{userInfo.first_name} sin profil</h1>
			<div className='profileOptions'>
				<h3>
					Velg informasjonen du vil vise. Du kan her oppdatere din
					informasjon som for eksempel epost, telefonnummer, navn
					eller passord.
				</h3>
				<div className='profileBtns'>
					<button onClick={() => setActiveSection('userInfo')}>
						Se din informasjon
					</button>
					<button
						onClick={() =>
							setActiveSection('profileChangePassword')
						}
					>
						Oppdater passord
					</button>
					<button onClick={() => setActiveSection('profileOrders')}>
						Se dine tidligere ordre
					</button>
				</div>
			</div>
			<div className='profileRenders'>
				{activeSection === 'userInfo' && (
					<div className='userInfo'>
						<h2>Du kan her se og endre brukerinformasjonen din.</h2>
						<form>
							<div className='column'>
								<label htmlFor='profileUsername'>
									Brukernavn
								</label>
								<input
									type='text'
									id='profileUsername'
									value={userInfo.username}
									readOnly
								/>
							</div>
							<div className='row'>
								<div className='column'>
									<label htmlFor='profileFirstName'>
										Fornavn
									</label>
									<input
										type='text'
										id='profileFirstName'
										name='first_name'
										value={userInfo.first_name}
										onChange={handleChange}
										readOnly={!isEditing}
									/>
								</div>
								<div className='column'>
									<label htmlFor='profileLastName'>
										Etternavn
									</label>
									<input
										type='text'
										id='profileLastName'
										name='last_name'
										value={userInfo.last_name}
										onChange={handleChange}
										readOnly={!isEditing}
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<label htmlFor='profileEmail'>Epost</label>
									<input
										type='text'
										id='profileEmail'
										name='email'
										value={userInfo.email}
										onChange={handleChange}
										readOnly={!isEditing}
									/>
								</div>
								<div className='column'>
									<label htmlFor='profilePhone'>
										Telefonnummer
									</label>
									<input
										type='text'
										id='profilePhone'
										name='telephone'
										value={userInfo.telephone}
										onChange={handleChange}
										readOnly={!isEditing}
									/>
								</div>
							</div>
							<div className='row'>
								<div className='column'>
									<label htmlFor='profileDepartment'>
										Kontor
									</label>
									<select
										name='department'
										id='profileDepartment'
										value={currentOffice || ''}
										onChange={handleChange}
										disabled={!isEditing}
									>
										<option value='' disabled hidden>
											Velg kontor
										</option>
										{offices.map((office) => (
											<option key={office} value={office}>
												{office}
											</option>
										))}
									</select>
								</div>
							</div>
						</form>
						{profileMessage && (
							<div className='profileMsg'>{profileMessage}</div>
						)}
						<div className='editButtons'>
							{isEditing ? (
								<>
									<button
										className='confBtn'
										onClick={handleConfirmClick}
									>
										Bekreft endring
									</button>
									<button
										className='abortBtn'
										onClick={handleAbortClick}
									>
										Avbryt
									</button>
								</>
							) : (
								<button
									className='changeBtn'
									onClick={handleEditClick}
								>
									Endre info
								</button>
							)}
						</div>
					</div>
				)}
				{activeSection === 'profileChangePassword' && (
					<div className='profileChangePassword'>
						<h2>Her kan du endre ditt passord.</h2>
						<form onSubmit={handlePasswordSubmit}>
							<div>
								<label htmlFor='profileCurrentPassword'>
									Nåværende passord
								</label>
								<input
									type='password'
									id='profileCurrentPassword'
									name='currentPassword'
									value={passwordData.currentPassword}
									onChange={handlePasswordChange}
								/>
							</div>
							<div>
								<label htmlFor='profileNewPassword'>
									Nytt passord
								</label>
								<input
									type='password'
									id='profileNewPassword'
									name='newPassword'
									value={passwordData.newPassword}
									onChange={handlePasswordChange}
								/>
							</div>
							<div>
								<label htmlFor='profileConfirmPassword'>
									Bekreft passord
								</label>
								<input
									type='password'
									id='profileConfirmPassword'
									name='confirmNewPassword'
									value={passwordData.confirmNewPassword}
									onChange={handlePasswordChange}
								/>
							</div>
							<button type='submit'>Oppdater passord</button>
						</form>
						{passwordMessage && (
							<div className='passwordMsg'>{passwordMessage}</div>
						)}
					</div>
				)}
				{activeSection === 'profileOrders' && (
					<div className='profileOrders'>
						<h2>DIne innsendte ordre</h2>
						<table>
							<thead>
								<tr>
									<th>PDF Navn</th>
									<th>Opprettet</th>
									<th>PDF</th>
								</tr>
							</thead>
							<tbody>
								{userPDFs.map((pdf) => (
									<tr key={pdf.id}>
										<td>{pdf.pdf_name}</td>
										<td>
											{new Date(
												pdf.created_at
											).toLocaleString()}
										</td>
										<td>
											<button
												type='button'
												className='pdf-download-btn'
												onClick={async () => {
													try {
														await downloadPdfById(
															pdf.id,
															pdf.pdf_name
														);
													} catch (err) {
														setError(
															'Kunne ikke laste ned PDF.'
														);
													}
												}}
												aria-label={`Last ned ${pdf.pdf_name}`}
											>
												<FaRegFilePdf size='20' />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
};

export default Profile;
